import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Cloud,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  FileText,
  Users,
  Download,
  Filter,
  Search,
  Eye,
  FolderOpen,
  Trash2,
  Loader2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { id: "passport", name: "Pasaporte" },
  { id: "previous_visa", name: "Visa Anterior" },
  { id: "employment_letter", name: "Carta Laboral" },
  { id: "bank_statements", name: "Estados de Cuenta" },
  { id: "income_proof", name: "Comprobante de Ingresos" },
  { id: "marriage_certificate", name: "Acta de Matrimonio" },
  { id: "birth_certificate", name: "Acta de Nacimiento" },
  { id: "address_proof", name: "Comprobante de Domicilio" },
  { id: "photo", name: "Fotografía" },
  { id: "other", name: "Otro" }
];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const AdminDocuments = () => {
  const [driveStatus, setDriveStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [clients, setClients] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchDriveStatus();
    fetchDocuments();
    fetchStats();
    fetchClients();
  }, []);

  const fetchDriveStatus = async () => {
    try {
      const response = await axios.get(`${API}/oauth/drive/status`);
      setDriveStatus(response.data);
    } catch (error) {
      setDriveStatus({ connected: false });
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/documents/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setClients(response.data.filter(u => u.role === "client"));
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const handleDownload = async (document) => {
    // For Cloudinary documents, open directly
    if (document.cloudinary_url) {
      window.open(document.cloudinary_url, "_blank");
      toast.success("Abriendo documento...");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/documents/${document.id}/download`, {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement("a");
      link.href = url;
      link.setAttribute("download", document.stored_name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Documento descargado");
    } catch (error) {
      toast.error("Error al descargar");
    }
  };

  const handleDelete = async (document) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${document.file_name}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    setDeletingId(document.id);
    try {
      await axios.delete(`${API}/documents/${document.id}`);
      // Refetch documents to ensure consistency (avoid UI freeze from state mutation)
      await fetchDocuments(selectedClient);
      toast.success("Documento eliminado");
      // Refresh stats
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar documento");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!doc.document_type_name?.toLowerCase().includes(query) && 
          !doc.file_name?.toLowerCase().includes(query) &&
          !doc.user_name?.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (filterType !== "all" && doc.document_type !== filterType) {
      return false;
    }
    if (filterClient !== "all" && doc.user_id !== filterClient) {
      return false;
    }
    return true;
  });

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="admin-documents-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy-900">
              Gestión de Documentos
            </h1>
            <p className="text-navy-500">
              Administra los documentos de todos los clientes
            </p>
          </div>
          <Button onClick={fetchDocuments} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Cloudinary Connection Card */}
        <div className={`rounded-xl border p-6 mb-6 ${
          driveStatus?.connected 
            ? "bg-emerald-50 border-emerald-200" 
            : "bg-amber-50 border-amber-200"
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                driveStatus?.connected ? "bg-emerald-100" : "bg-amber-100"
              }`}>
                <Cloud className={`w-6 h-6 ${
                  driveStatus?.connected ? "text-emerald-600" : "text-amber-600"
                }`} />
              </div>
              <div>
                <h3 className="font-semibold text-navy-900">Cloudinary</h3>
                <p className={`text-sm ${
                  driveStatus?.connected ? "text-emerald-700" : "text-amber-700"
                }`}>
                  {driveStatus?.connected 
                    ? `Conectado y listo para subir documentos`
                    : "No configurado - Configura las credenciales de Cloudinary"
                  }
                </p>
              </div>
            </div>
            {driveStatus?.connected && (
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Conectado</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Total Documentos</p>
                <p className="text-2xl font-bold text-navy-900">
                  {stats?.total_documents || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Clientes con Docs</p>
                <p className="text-2xl font-bold text-navy-900">
                  {stats?.total_clients_with_docs || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Tipos de Doc</p>
                <p className="text-2xl font-bold text-navy-900">
                  {stats?.by_type?.length || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                driveStatus?.connected ? "bg-emerald-100" : "bg-red-100"
              }`}>
                <Cloud className={`w-5 h-5 ${
                  driveStatus?.connected ? "text-emerald-600" : "text-red-600"
                }`} />
              </div>
              <div>
                <p className="text-sm text-navy-500">Cloudinary</p>
                <p className={`text-lg font-bold ${
                  driveStatus?.connected ? "text-emerald-600" : "text-red-600"
                }`}>
                  {driveStatus?.connected ? "Conectado" : "Desconectado"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                placeholder="Buscar por nombre, tipo o cliente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Tipo de documento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {DOCUMENT_TYPES.map(type => (
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los clientes</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Documents Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
          </div>
        ) : filteredDocuments.length > 0 ? (
          <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-navy-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Documento</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Cliente</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Tipo</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Tamaño</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Fecha</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {filteredDocuments.map(doc => (
                  <tr key={doc.id} className="hover:bg-navy-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-navy-400" />
                        <span className="text-sm text-navy-900 truncate max-w-[200px]">
                          {doc.file_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-navy-700">{doc.user_name}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-navy-100 rounded-full text-xs text-navy-700">
                        {doc.document_type_name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-navy-500">{formatFileSize(doc.file_size)}</td>
                    <td className="px-4 py-3 text-sm text-navy-500">{formatDate(doc.uploaded_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {doc.web_view_link && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(doc.web_view_link, "_blank")}
                            title="Ver documento"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(doc)}
                          title="Descargar"
                        >
                          <Download className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(doc)}
                          disabled={deletingId === doc.id}
                          title="Eliminar"
                          className="hover:bg-red-50"
                        >
                          {deletingId === doc.id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-red-500" />
                          ) : (
                            <Trash2 className="w-4 h-4 text-red-500" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-navy-100">
            <FolderOpen className="w-16 h-16 text-navy-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-navy-700 mb-2">
              No hay documentos
            </h3>
            <p className="text-navy-500">
              {!driveStatus?.connected 
                ? "Configura Cloudinary para que los clientes puedan subir documentos"
                : "Los clientes aún no han subido documentos"
              }
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminDocuments;
